import { useEffect, useState, useMemo, useContext, useRef } from 'react';

import '../App.css';
import Carrito from '../components/Carrito';
import GaleriaImagenes from '../components/GaleriaImagenes';
import FiltrosBusqueda from '../components/FiltrosBusqueda';
import SeccionContacto from '../components/SeccionContacto';
import MasVisitados from '../components/MasVisitados';
import SeccionCategorias from '../components/SeccionCategorias';
import BannerPromociones from '../components/BannerPromociones';
import { CarritoContext } from '../context/CarritoContext';
import { useFondo } from '../components/SelectorFondo';

export default function Tienda() {
  useFondo();
  const [muebles, setMuebles]           = useState([]);
  const [categorias, setCategorias]     = useState([]);
  const [filtros, setFiltros]           = useState({ busqueda: '', categoria: '', categoriasMatch: [], precioMin: '', precioMax: '' });
  const [categoriaClickeada, setCategoriaClickeada] = useState(undefined);
  const [mostrarCarrito, setMostrarCarrito] = useState(false);
  const [mensajePago, setMensajePago]   = useState(null); // { tipo, pedido }
  const productosRef = useRef(null);
  const { agregarProducto, limpiarCarrito, productosCarrito } = useContext(CarritoContext);
  const totalUnidades = productosCarrito.reduce((acc, item) => acc + item.cantidad, 0);

  // Leer resultado de retorno de MercadoPago
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pago   = params.get('pago');
    const pedido = params.get('pedido');
    if (pago) {
      if (pago === 'aprobado') limpiarCarrito();
      setMensajePago({ tipo: pago, pedido });
      // Limpiar la URL sin recargar la página
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    fetch('/api/productos')
      .then(r => r.json())
      .then(data => setMuebles(data))
      .catch(err => console.error('Error cargando productos:', err));
    fetch('/api/categorias')
      .then(r => r.json())
      .then(data => setCategorias(Array.isArray(data) ? data : []))
      .catch(() => setCategorias([]));
  }, []);

  const handleSeleccionarCategoria = (nombre) => {
    setCategoriaClickeada(nombre);
    if (nombre) {
      productosRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const mueblesFiltrados = useMemo(() => {
    return muebles.filter(m => {
      if (filtros.busqueda && !m.nombre.toLowerCase().includes(filtros.busqueda.toLowerCase())) return false;
      if (filtros.categoriasMatch?.length > 0 && !filtros.categoriasMatch.includes(m.categoria)) return false;
      if (filtros.precioMin !== '' && m.precio < Number(filtros.precioMin)) return false;
      if (filtros.precioMax !== '' && m.precio > Number(filtros.precioMax)) return false;
      return true;
    });
  }, [muebles, filtros]);

  return (
    <div className="app-container">
      <header className="app-header">
        <div />

        <div className="app-header-marca">
          <h1 className="marca-nombre">lucs<span>Good</span></h1>
          <p className="marca-sub">muebles &amp; deco</p>
        </div>

        <div className="header-acciones">
          <button
            className="btn-ver-carrito"
            onClick={() => setMostrarCarrito(!mostrarCarrito)}
          >
            🛒 Ver Carrito{totalUnidades > 0 && <span className="carrito-badge">{totalUnidades}</span>}
          </button>
        </div>
      </header>
      <BannerPromociones />

      {/* Banner resultado de pago MP */}
      {mensajePago && (
        <div className={`mp-banner mp-banner-${mensajePago.tipo}`}>
          <span className="mp-banner-icono">
            {mensajePago.tipo === 'aprobado' ? '✓' : mensajePago.tipo === 'pendiente' ? '⏳' : '✕'}
          </span>
          <div className="mp-banner-texto">
            {mensajePago.tipo === 'aprobado' && (
              <>
                <strong>¡Pago aprobado!</strong> Tu pedido <em>{mensajePago.pedido}</em> fue confirmado.
                Recibirás un email de confirmación.
              </>
            )}
            {mensajePago.tipo === 'pendiente' && (
              <>
                <strong>Pago pendiente.</strong> Tu pedido <em>{mensajePago.pedido}</em> está siendo procesado.
                Te avisaremos cuando se confirme.
              </>
            )}
            {mensajePago.tipo === 'error' && (
              <>
                <strong>El pago no se completó.</strong> Podés intentarlo de nuevo desde el carrito.
              </>
            )}
          </div>
          <button className="mp-banner-cerrar" onClick={() => setMensajePago(null)}>✕</button>
        </div>
      )}

      {mostrarCarrito ? (
        <Carrito onCerrar={() => setMostrarCarrito(false)} />
      ) : (
        <>
          <MasVisitados />
          <SeccionCategorias
            categorias={categorias}
            categoriaActiva={filtros.categoria}
            onSeleccionar={handleSeleccionarCategoria}
          />
          <div ref={productosRef} />
          <FiltrosBusqueda onFiltrar={setFiltros} categoriaExterna={categoriaClickeada} />
          {mueblesFiltrados.length === 0 && (
            <p className="filtros-sin-resultados">No se encontraron productos con esos filtros.</p>
          )}
          <div className="productos-grid">
            {mueblesFiltrados.map(mueble => (
              <div key={mueble._id} className={`producto-card${mueble.stock === 0 ? ' sin-stock' : ''}`}>
                <div className="producto-imagen-wrap">
                  <GaleriaImagenes
                    imagen={mueble.imagen}
                    imagenes={mueble.imagenes}
                    nombre={mueble.nombre}
                    productoId={mueble._id}
                  />
                  {mueble.stock === 0 && (
                    <span className="badge-sin-stock">Sin stock</span>
                  )}
                </div>
                <div className="producto-contenido">
                  <h3 className="producto-nombre">{mueble.nombre}</h3>
                  {mueble.categoria && (
                    <p className="producto-categoria-tag">{mueble.categoria}</p>
                  )}
                  {mueble.descripcion && (
                    <p className="producto-descripcion">{mueble.descripcion}</p>
                  )}
                  <p className="producto-precio">${mueble.precio?.toLocaleString('es-AR')}</p>
                  <div className="producto-botones">
                    <button
                      className="btn-carrito"
                      onClick={() => agregarProducto(mueble)}
                      disabled={mueble.stock === 0}
                    >
                      🛒 Agregar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      <SeccionContacto />
    </div>
  );
}
